/**
 * Libraries fetched on demand — and what to do when the fetch fails.
 *
 * jsPDF (~360KB) and SheetJS (~430KB) are only downloaded when someone
 * actually exports, which keeps them out of the till's first paint. The cost
 * is that an export can fail in a way nothing else in the app can: the file is
 * fetched from the server at the moment of the click.
 *
 * Two things cause that, and they need different answers:
 *
 *   1. A dropped request on a booth's tethered connection. Worth one retry —
 *      the file is still there.
 *   2. A deploy while the tab was open. Vite names every chunk after its
 *      contents, so a deploy can rename the file; the name this tab knows is
 *      then gone for good and no retry will ever find it. Only a page reload
 *      picks up the new name.
 *
 * The second case used to surface as "Failed to fetch dynamically imported
 * module" and read like a permanently broken feature. It is named here so the
 * export menus can offer the one thing that fixes it.
 *
 * (vite.config.ts keeps these vendor chunks out of the app's own hash, so a
 * deploy no longer renames them for an app-code change. This is the belt to
 * that's braces — a chunk can still churn when the library itself is upgraded.)
 */

export class ChunkLoadError extends Error {
  constructor(what: string, extra = '') {
    super(
      `Gagal memuat modul ${what}. Kalau aplikasi baru saja diperbarui, muat ulang ` +
        `halaman lalu coba lagi.${extra ? ` ${extra}` : ''}`,
    );
    this.name = 'ChunkLoadError';
  }
}

/** True for the failure a page reload fixes. Survives module boundaries. */
export const isChunkLoadError = (e: unknown): boolean =>
  e instanceof Error && e.name === 'ChunkLoadError';

/** Import a chunk, retrying once, and name the failure when it stays broken. */
export async function loadChunk<T>(
  load: () => Promise<T>,
  what: string,
  extra = '',
): Promise<T> {
  try {
    return await load();
  } catch {
    // The usual cause is a container swap mid-deploy or a blip, both of which
    // are over by now. A renamed chunk fails again here, and that is the point.
    await new Promise((r) => setTimeout(r, 800));
    try {
      return await load();
    } catch {
      throw new ChunkLoadError(what, extra);
    }
  }
}

/** jsPDF, loaded once per session. */
let jspdfPromise: Promise<typeof import('jspdf')> | null = null;

export function loadJsPdf(): Promise<typeof import('jspdf')> {
  if (!jspdfPromise) {
    jspdfPromise = loadChunk(() => import('jspdf'), 'PDF').catch((e) => {
      jspdfPromise = null; // let the next attempt start fresh
      throw e;
    });
  }
  return jspdfPromise;
}
